// Single source of truth for reading a video's per-platform publication status.
//
// Background: the Comprehensive Platform Matrix saves everything under
// `video.platform_status[key] = { status, posted_date, price, notes, timeline }`
// using keys like `onlyfans_vip`, `faphouse`, `top4fans`, `darkfans`, ...
// Older/seed data used flat top-level fields (`faphouse_status`, `jff_posted_at`)
// and a legacy `platform_status` shape (`onlyfans`, `store`, `twitter`).
// The grid card / preview / CSV used to read the flat fields directly, which is
// why the Matrix edits (and their dates) never showed up. This helper reads the
// Matrix dict first and falls back to legacy fields so everything stays in sync.

export const STATUS_LABELS = {
  to_be_scheduled: "To Be Scheduled",
  scheduled: "Scheduled",
  uploaded: "Uploaded",
  published: "Published",
  rejected: "Rejected",
  presale_dm: "Presale DM",
  presale_post: "Presale Post",
  presale: "Presale",
  live_on_subs: "Live On Subs",
  live_sub: "Live On Subs",
  removed_by_platform: "Removed By Platform",
  needs_to_re_edit: "Needs To Re-Edit",
  re_edited: "Re-Edited",
  ready_to_re_publish: "Ready To Re-Publish",
  re_published: "Re-Published",
  removed_dmca: "Removed DMCA",
  removed_coactor_retired: "Removed (Co-actor Retired)",
  post_to_inner_circle: "Post to Inner Circle",
  promo_posted: "Promo Posted",
  not_posted: "Not Posted",
  removed: "Removed",
  on_sale: "On Sale",
  free_members: "Free Members",
  basic_members: "Basic Members",
  vip_members: "VIP Members",
  bts: "BTS",
  not_available: "Not Available",
};

// Ordered platform list matching the Matrix. `legacy` = flat field prefixes,
// `psAlias` = older platform_status keys to also check.
export const PLATFORM_DISPLAY = [
  { key: "members_area",  label: "🔐 Members", color: "#FF10F0" },
  { key: "onlyfans_vip",  label: "OF VIP",  color: "#00AFF0", legacy: ["of_vip"], psAlias: ["onlyfans"] },
  { key: "onlyfans_free", label: "OF Free", color: "#22c55e", legacy: ["of_free"] },
  { key: "justforfans",   label: "JFF",     color: "#e11d48", legacy: ["jff"] },
  { key: "rawfuckclub",   label: "RFC",     color: "#f59e0b", legacy: ["rfc"] },
  { key: "faphouse",      label: "Faphouse", color: "#8b5cf6", legacy: ["faphouse"] },
  { key: "top4fans",      label: "T4F",     color: "#fbbf24", legacy: ["top4fans"] },
  { key: "darkfans",      label: "Dark",    color: "#a16207", legacy: ["darkfans"] },
  { key: "video_store",   label: "🛒 Store", color: "#10b981", psAlias: ["store"] },
  { key: "x_twitter",     label: "𝕏",       color: "#1DA1F2", psAlias: ["twitter"] },
  { key: "instagram",     label: "IG",      color: "#E1306C" },
  { key: "tiktok",        label: "TikTok",  color: "#ff0050" },
  { key: "bluesky",       label: "Bluesky", color: "#0085ff" },
  { key: "xhamster",      label: "XHamster", color: "#ff9933" },
];

const INACTIVE = new Set(["", "not_published", "not_posted", "not_available", "none", "n/a", "na", "no", null, undefined]);

export function isActiveStatus(status) {
  if (status === null || status === undefined) return false;
  return !INACTIVE.has(String(status).trim().toLowerCase());
}

export function statusLabel(status) {
  if (!status) return "";
  return STATUS_LABELS[status] || String(status).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

export function fmtDate(d) {
  if (!d) return "";
  const s = String(d);
  const dt = new Date(s.length <= 10 ? s + "T00:00:00" : s);
  if (isNaN(dt.getTime())) return s;
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// Read status/date/price/notes for one platform, preferring the Matrix dict.
export function getPlatformStatus(video, entry) {
  const ps = video?.platform_status || {};
  const candidates = [ps[entry.key], ...(entry.psAlias || []).map(a => ps[a])].filter(Boolean);

  let status = "";
  let posted_date = "";
  let price = null;
  let notes = "";
  let timeline = [];
  let url = "";
  let tier = "";
  let customLabel = "";

  for (const c of candidates) {
    if (!customLabel && c.label) customLabel = c.label;
    if (!url && c.url) url = c.url;
    if (!tier && c.tier) tier = c.tier;
    if (!status && isActiveStatus(c.status)) status = c.status;
    if (!posted_date && c.posted_date) posted_date = c.posted_date;
    if ((price === null || price === undefined) && c.price !== null && c.price !== undefined && c.price !== "") price = c.price;
    if (!notes && c.notes) notes = c.notes;
    if ((!timeline || timeline.length === 0) && Array.isArray(c.timeline)) timeline = c.timeline;
    if (!status && c.status) status = c.status; // keep even a default so we can fall back below
  }

  // Legacy flat-field fallback
  if (!url && LEGACY_LINK[entry.key] && video?.[LEGACY_LINK[entry.key]]) url = video[LEGACY_LINK[entry.key]];
  const flat = entry.legacy || [];
  for (const fk of flat) {
    if (!isActiveStatus(status) && isActiveStatus(video?.[`${fk}_status`])) status = video[`${fk}_status`];
    if (!posted_date && (video?.[`${fk}_posted_date`] || video?.[`${fk}_posted_at`])) {
      posted_date = video[`${fk}_posted_date`] || video[`${fk}_posted_at`];
    }
    if ((price === null || price === undefined) && video?.[`${fk}_price`] != null) price = video[`${fk}_price`];
  }

  return { key: entry.key, label: customLabel || entry.label, color: entry.color, status, posted_date, price, notes, timeline, url, tier };
}

const LEGACY_LINK = { onlyfans_vip: "onlyfans_vip_link", onlyfans_free: "onlyfans_free_link", faphouse: "faphouse_link", justforfans: "justforfans_link" };

const GREEN = new Set(["published", "re_published", "live_on_subs", "live_sub", "promo_posted", "free_members", "basic_members", "vip_members", "bts", "on_sale"]);
const YELLOW = new Set(["scheduled", "to_be_scheduled", "uploaded", "processing", "presale", "presale_dm", "presale_post", "ready_to_re_publish", "re_edited", "post_to_inner_circle"]);

// green = published, yellow = scheduled/processing, red = not published / error / needs attention
export function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (GREEN.has(s)) return "green";
  if (YELLOW.has(s)) return "yellow";
  return "red";
}

// A platform counts as linked once it has a URL, a real status or a publish date.
export function getLinkedPlatforms(video) {
  return PLATFORM_DISPLAY
    .map(entry => getPlatformStatus(video, entry))
    .filter(row => row.url || isActiveStatus(row.status) || row.posted_date);
}

// All platforms that have a meaningful (non-default) status, in display order.
export function getVideoPlatformRows(video) {
  return PLATFORM_DISPLAY
    .map(entry => getPlatformStatus(video, entry))
    .filter(row => isActiveStatus(row.status));
}
